import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Cake, Briefcase, Users, Wine, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { PopIn, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";
import { useBookNow } from "@/hooks/useBookNow";

// Import your existing images from wherever they are
// Option 1: If images are in src/assets/images/
import banner1 from "@/assets/images/banner_1.jpg";
import banner2 from "@/assets/images/banner_2.jpg";
import banner3 from "@/assets/images/banner_3.jpg";
import banner4 from "@/assets/images/banner_4.jpg";



const tiles = [
  { 
    to: "/track", 
    k: "track", 
    n: "", 
    img: banner1 // Your existing track image
  },
  { 
    to: "/fleet", 
    k: "fleet", 
    n: "", 
    img: banner2 // Your existing fleet image
  },
  { 
    to: "/pricing", 
    k: "pricing", 
    n: "", 
    img: banner3 // Your existing pricing image
  },
  { 
    to: "/experience", 
    k: "experience", 
    n: "", 
    img: banner4 // Your existing experience image
  },
];

export default function HomePage() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <HomeTiles />
      <EventsTeaser />
      <CTASection />
    </main>
  );
}

function HomeTiles() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const [loadedImages, setLoadedImages] = useState({});

  // Preload images
  useEffect(() => {
    tiles.forEach(tile => {
      const img = new Image();
      img.src = tile.img;
      img.onload = () => {
        setLoadedImages(prev => ({ ...prev, [tile.to]: true }));
      };
    });
  }, []);

  return (
    <section ref={ref} className="py-24 md:py-36 bg-[#050505] overflow-hidden" data-testid="home-tiles">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <motion.div style={{ y: titleY }} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <PopIn className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-[#FF2D2D]" />
              <span className="font-mono-display text-[11px] tracking-[0.3em] text-[#FF2D2D] uppercase">
                {lang === "en" ? "Explore" : "Разгледай"}
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tighter leading-[0.95]">
              {lang === "en" ? (
                <>The whole circuit. <span className="text-white/30">One scroll away.</span></>
              ) : (
                <>Цялата писта. <span className="text-white/30">На един скрол.</span></>
              )}
            </h2>
          </PopIn>
        </motion.div>

        <StaggerGroup className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {tiles.map((tile) => (
            <StaggerItem key={tile.to}>
              <button
                onClick={() => { navigate(tile.to); window.scrollTo(0, 0); }}
                className="group relative aspect-[3/4] overflow-hidden bg-[#0a0a0a] text-left w-full"
                data-testid={`home-tile-${tile.k}`}
              >
                <div className="absolute inset-0 bg-[#0a0a0a]">
                  {!loadedImages[tile.to] && (
                    <div className="w-full h-full animate-pulse bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]" />
                  )}
                  <img
                    src={tile.img}
                    alt={t.nav[tile.k]}
                    className={`w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110 ${
                      loadedImages[tile.to] ? 'opacity-100' : 'opacity-0'
                    }`}
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-0 border border-transparent group-hover:border-[#FF2D2D]/60 transition-colors" />
                <div className="relative h-full flex flex-col justify-between p-3 sm:p-4 md:p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-display text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] text-[#FF2D2D] uppercase">
                      {tile.n}
                    </span>
                    <ArrowUpRight size={14} className="text-white/60 group-hover:text-[#FF2D2D] group-hover:rotate-45 transition-all" />
                  </div>
                  <h3 className="font-display text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white group-hover:text-[#FF2D2D] transition-colors leading-tight">
                    {t.nav[tile.k]}
                  </h3>
                </div>
              </button>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function EventsTeaser() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const eventTypes = [
    { id: "birthday",     icon: Cake,       en: "Birthdays",      bg: "Рождени дни" },
    { id: "corporate",    icon: Briefcase,  en: "Corporate",      bg: "Корпоративни" },
    { id: "team_building",icon: Users,      en: "Team Building",  bg: "Тиймбилдинг" },
    { id: "bachelor",     icon: Wine,       en: "Bachelor Party", bg: "Ергенско" },
    { id: "other",        icon: Sparkles,   en: "Other",          bg: "Друго" },
  ];

  const openEvents = (type) => {
    navigate(`/events?type=${type}#event-form`);
  };

  return (
    <section className="relative py-20 md:py-32 bg-[#070707] overflow-hidden" data-testid="events-teaser">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-10 md:mb-14">
          <PopIn className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="block w-8 h-px bg-[#FF2D2D]" />
              <span className="font-mono-display text-[11px] tracking-[0.3em] text-[#FF2D2D] uppercase">
                {lang === "en" ? "Private events" : "Частни събития"}
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tighter leading-[0.92]">
              {lang === "en" ? (
                <>Book the whole track. <span className="text-white/30">Throw a race-day.</span></>
              ) : (
                <>Резервирай цялата писта. <span className="text-white/30">Запали рождения си ден.</span></>
              )}
            </h2>
          </PopIn>
          <PopIn className="md:col-span-5" delay={0.15}>
            <p className="text-base md:text-lg text-white/55 font-light leading-relaxed">
              {lang === "en"
                ? "Birthdays, corporate offsites, team-buildings, bachelor parties — pick your format, we run the rest."
                : "Рождени дни, корпоративни събития, тиймбилдинг, ергенско — избираш формата, ние правим останалото."}
            </p>
          </PopIn>
        </div>

        <div className="flex flex-wrap gap-2.5 md:gap-3 mb-10">
          {eventTypes.map((evt, i) => {
            const Icon = evt.icon;
            return (
              <motion.button
                key={evt.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                onClick={() => openEvents(evt.id)}
                className="group inline-flex items-center gap-2 px-4 py-2.5 md:px-5 md:py-3 text-xs md:text-sm font-bold tracking-[0.14em] uppercase border border-white/15 text-white hover:bg-[#FF2D2D] hover:border-[#FF2D2D] hover:text-white transition-all"
                data-testid={`home-event-${evt.id}`}
              >
                <Icon size={14} className="text-[#FF2D2D] group-hover:text-white transition-colors" />
                {lang === "bg" ? evt.bg : evt.en}
              </motion.button>
            );
          })}
        </div>

        <PopIn>
          <button
            onClick={() => openEvents("birthday")}
            className="inline-flex items-center gap-3 bg-[#FF2D2D] text-white px-7 py-4 text-sm font-bold tracking-[0.16em] uppercase hover:bg-white hover:text-black transition-colors"
            data-testid="home-events-cta"
          >
            {lang === "en" ? "Plan your event" : "Планирай събитие"}
            <ArrowUpRight size={16} />
          </button>
        </PopIn>
      </div>
    </section>
  );
}

function CTASection() {
  const { t, lang } = useLang();
  const bookNow = useBookNow();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["20%", "-20%"]);

  return (
    <section ref={ref} className="relative py-20 md:py-28 bg-[#FF2D2D] overflow-hidden" data-testid="home-cta">
      <motion.div style={{ x }} className="absolute right-0 top-0 bottom-0 w-1/2 checker opacity-10" />
      <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <PopIn className="md:max-w-2xl">
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
            {lang === "en" ? "Ready to feel the line?" : "Готов ли си да усетиш скоростта?"}
          </h2>
        </PopIn>
        <PopIn delay={0.15}>
          <button
            onClick={bookNow}
            className="inline-flex items-center gap-3 bg-black text-white px-7 py-4 text-sm font-bold tracking-[0.16em] uppercase hover:bg-white hover:text-black transition-colors"
            data-testid="home-cta-book"
          >
            {t.nav.book}
            <ArrowUpRight size={16} />
          </button>
        </PopIn>
      </div>
    </section>
  );
}